<script setup lang="ts">
import ReorderableTable from '@/components/ReorderableTable.vue';
import type { ProjectDto } from '@/services/types/api.types';
import { useCommunityStore, useProjectStore, useProjectTemplateStore } from '@/stores';
import { storeToRefs } from 'pinia';
import { useQuasar } from 'quasar';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import CreateProjectTemplateDialog from '@/components/dialogs/CreateProjectTemplateDialog.vue';
import CreateProjectDialog from '@/components/dialogs/CreateProjectDialog.vue';
import { Constants } from '@/constants';
import { getTranslation } from '@/composables/data-display-utils';
import { confirmDelete } from '@/helpers/dialogs.helper';

const router = useRouter();
const { t } = useI18n();
const $q = useQuasar();

const communityStore = useCommunityStore();
const projectStore = useProjectStore();
const projectTemplateStore = useProjectTemplateStore();
projectTemplateStore.fetchAll();

const { community } = storeToRefs(communityStore);
const { isLoading, projectTemplates } = storeToRefs(projectTemplateStore);

const actionMenuItems = computed(() => [
  { action: 'create-project', label: t('projectTemplate.createProject') },
  { action: 'delete-row', label: t('global.label.delete') },
]);

const columns = computed(() => [
  {
    name: 'title',
    label: t('global.label.title'),
    field: (row: ProjectDto) => getTranslation(row, 'title', row.language_enabled),
    align: 'left',
  },
]);

function getDefaultLanguageEnabled() {
  return [community.value?.default_language ?? Constants.DefaultLocale];
}

const handleRowClick = (row: ProjectDto) => {
  router.push({ name: 'project_template_details', params: { id: row.id } });
};

const handleDeleteRow = (row: ProjectDto) => {
  if (!row.id) {
    console.warn('Row does not have an id');
    return;
  }

  confirmDelete(t('projectTemplate.name')).onOk(() => {
    projectTemplateStore.delete(row.id!);
  });
};

const handleActions = async (action: any) => {
  switch (action.action) {
    case 'create-project':
      const projectTemplateId = action.payload.row.id;
      await projectTemplateStore.fetchById(projectTemplateId);
      const projectTemplate = projectTemplateStore.getItem(projectTemplateId);
      $q.dialog({
        component: CreateProjectDialog,
        componentProps: {
          project: projectStore.create({
            title: projectTemplate?.title,
            description: projectTemplate?.description,
            cloneFromId: projectTemplate?.id,
            languageEnabled: projectTemplate?.language_enabled ?? getDefaultLanguageEnabled(),
          }),
          languageEnabled: projectTemplate?.language_enabled ?? getDefaultLanguageEnabled(),
        },
      });
      break;
  }
};

const handleAddRow = () => {
  const projectTemplate: ProjectDto = projectTemplateStore.create({ languageEnabled: getDefaultLanguageEnabled() });
  $q.dialog({
    component: CreateProjectTemplateDialog,
    componentProps: {
      projectTemplate,
      languageEnabled: getDefaultLanguageEnabled(),
    },
  });
};
</script>

<template>
  <div>
    <h1 class="title">{{ t('projectTemplate.projectTemplates') }}</h1>
    <reorderable-table
      :rows="projectTemplates"
      :columns="columns"
      :loading="isLoading"
      itemKey="id"
      :disableOrderable="true"
      :disableDeleteButton="true"
      :pagination-enabled="false"
      :action-menu-items="actionMenuItems"
      @row-click="handleRowClick"
      @delete-row="handleDeleteRow"
      @add-row="handleAddRow"
      @actions="handleActions"
    />
  </div>
</template>
<style lang="scss"></style>
